import { t } from "@lingui/macro";
import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useState } from "react";
import { Platform } from "react-native";
import { useFragment } from "react-relay";

import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { Button } from "@whoosh/design-system/src/components/controls";
import { PopUpModal } from "@whoosh/design-system/src/components/overlay/PopUpModal";
import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { EventSignUpProcessSteps } from "@whoosh/features/src/Events/features/AddorEditSignUp";
import { getTimeUntilDateString } from "@whoosh/utilities/src/dates";

import { currentProfileAtom } from "../../../../features/Club/atoms";
import { useFlags } from "../../../../utils/launchDarkly";
import { openEventSignUpModalAtom } from "../../atoms/actions/openEventSignUpModal/openEventSignUpModalAtom";
import { useEventDetailLoginRedirect } from "../../hooks/useEventDetailLoginRedirect";
import { EventDetailCancelWindow } from "../EventDetailCancelWindow";
import { EventDetailJoinEvent_event$key } from "./__generated__/EventDetailJoinEvent_event.graphql";

type EventDetailJoinEventProps = {
  eventRef: EventDetailJoinEvent_event$key;
};

export const EventDetailJoinEvent = ({
  eventRef,
}: EventDetailJoinEventProps) => {
  const route =
    useRoute<RouteProp<ClubStackNavigatorParamList, "EventDetail">>();
  const { joinEvent, openSignupStep } = route.params;
  const event = useFragment(EventDetailJoinEventFragmentString, eventRef);
  const eventSignupWindows = event.clientEligibleSignUpWindows;
  const accessStatusForClient = event.accessStatusForClient;

  const signUpStatusForClient = event.signUpStatusForClient;

  const [isJoinEventModalOpen, setIsJoinEventModalOpen] = useState(false);

  const currentProfile = useAtomValue(currentProfileAtom);
  const registrantId = currentProfile?.memberId;
  const { newPatronEventBookingFlow } = useFlags();

  const isGroupEvent =
    event?.maxGuestCount !== 0 ||
    event?.maxMemberCount !== 1 ||
    event?.minMemberCount !== 1 ||
    event?.minGuestCount !== 0;

  const openEventSignUpModal = useSetAtom(openEventSignUpModalAtom);
  const handleGroupEventRegistration = useCallback(() => {
    openEventSignUpModal();
  }, [openEventSignUpModal]);

  const handleAddToWaitlist = useCallback(() => {
    if (registrantId) {
      handleGroupEventRegistration();
    }
  }, [handleGroupEventRegistration, registrantId]);

  const isClosed = accessStatusForClient === "CLOSED";
  const isComplete = accessStatusForClient === "COMPLETE";

  const closesAtWindows = eventSignupWindows?.filter((suw) => {
    return !!suw?.closesAt;
  });

  const opensAtWindows = eventSignupWindows?.filter((suw) => {
    return !!suw?.opensAt;
  });

  const cancelAtWindows = eventSignupWindows?.filter((suw) => {
    return !!suw?.cancelDeadlineAt;
  });

  const closesAtWindow =
    !!closesAtWindows?.length &&
    !!closesAtWindows?.[0]?.closesAt &&
    getTimeUntilDateString(closesAtWindows?.[0]?.closesAt);

  const opensAtWindow =
    !!opensAtWindows?.length &&
    !!opensAtWindows?.[0]?.opensAt &&
    getTimeUntilDateString(opensAtWindows?.[0]?.opensAt);

  const cancelAtWindow =
    !!cancelAtWindows?.length && !!cancelAtWindows?.[0]?.cancelDeadlineAt
      ? cancelAtWindows?.[0]?.cancelDeadlineAt
      : null;

  const handleSignIn = useEventDetailLoginRedirect(
    event.eventSeries?.facility?.club?.slug ?? "",
    event.id ?? ""
  );

  const handleJoinPress = useCallback(() => {
    /**
     * The timeout exists because react native modal gets salty
     * if we try to switch which modal is open in the same process.
     *
     * This timeout just pushes opening the second modal off until the first
     * one completes and avoids accidentally locking the screen.
     *
     *-- https://app.shortcut.com/whoosh/story/16149/ensure-we-can-toggle-between-two-modals
     */

    if (currentProfile?.memberId) {
      setIsJoinEventModalOpen(false);
      setTimeout(() => {
        handleGroupEventRegistration();
      }, 0);
    } else {
      handleSignIn(true);
    }
  }, [currentProfile?.memberId, handleGroupEventRegistration, handleSignIn]);

  const showConfirmationModalSignUp =
    !newPatronEventBookingFlow &&
    !isGroupEvent &&
    !event.memberSignUpProxyAllowed &&
    !!currentProfile?.memberId;

  const canJoinEvent =
    signUpStatusForClient === "NONE" &&
    accessStatusForClient === "SIGNUP_AVAILABLE" &&
    !isClosed;

  const canAddToWaitlist =
    signUpStatusForClient === "NONE" &&
    accessStatusForClient === "WAITLIST_AVAILABLE" &&
    !isClosed;

  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList, "EventDetail">>();

  useEffect(() => {
    if (
      joinEvent &&
      Platform.OS === "web" &&
      (canJoinEvent || canAddToWaitlist)
    ) {
      if (showConfirmationModalSignUp) {
        setIsJoinEventModalOpen(true);
      } else if (openSignupStep) {
        openEventSignUpModal({
          overrideStep: openSignupStep as EventSignUpProcessSteps,
        });
      } else {
        handleJoinPress();
      }
      // Clear the joinEvent parameter after triggering the flow
      navigation.setParams({ joinEvent: undefined, openSignupStep: undefined });
    }
  }, [
    joinEvent,
    openSignupStep,
    canJoinEvent,
    canAddToWaitlist,
    showConfirmationModalSignUp,
    handleJoinPress,
    openEventSignUpModal,
    navigation,
  ]);

  return (
    <Box bg="surface-primary">
      <VStack gap={{ base: 5, lg: 4 }} justifyContent="center">
        {signUpStatusForClient === "NONE" &&
        accessStatusForClient === "PENDING" ? (
          <>
            <Button
              width="100%"
              isDisabled={true}
              accessibilityLabel={t`Join Event`}
              testID="event-detail-join-event-button"
            >{t`Join Event`}</Button>
            {opensAtWindow && (
              <Text
                color="text-secondary"
                variant="mainSmallSemiBold"
                mb={{ base: 4, md: 0 }}
                textAlign="center"
              >
                {`${t`Event signup opens`} ${opensAtWindow}`}
              </Text>
            )}
          </>
        ) : null}

        {signUpStatusForClient === "NONE" &&
        accessStatusForClient === "SIGNUP_AVAILABLE" &&
        !isClosed ? (
          <>
            <Button
              width="100%"
              accessibilityLabel={t`Join Event`}
              testID="event-detail-join-event-button"
              onPress={
                showConfirmationModalSignUp
                  ? () => setIsJoinEventModalOpen(true)
                  : handleJoinPress
              }
            >{t`Join Event`}</Button>
            {closesAtWindow && (
              <Text
                color="text-secondary"
                variant="mainSmallSemiBold"
                mb={{ base: 4, md: 0 }}
                textAlign="center"
              >
                {`${t`Event signup closes`} ${closesAtWindow}`}
              </Text>
            )}
          </>
        ) : null}

        {signUpStatusForClient === "NONE" &&
        accessStatusForClient === "WAITLIST_AVAILABLE" &&
        !isClosed ? (
          <>
            <Button
              width="100%"
              accessibilityLabel={t`Join Waitlist`}
              testID="event-detail-join-waitlist-button"
              onPress={handleAddToWaitlist}
            >{t`Join Waitlist`}</Button>
            <Text
              color="text-secondary"
              variant="mainSmallSemiBold"
              mb={{ base: 4, md: 0 }}
              textAlign="center"
            >
              {t`Waitlist is available`}
            </Text>
            {closesAtWindow && (
              <Text
                color="text-secondary"
                mb={{ base: 4, md: 0 }}
                textAlign="center"
                variant="mainSmallSemiBold"
              >
                {`${t`Event signup closes`} ${closesAtWindow}`}
              </Text>
            )}
          </>
        ) : null}

        {!isClosed &&
        accessStatusForClient === "FULL" &&
        !["CONFIRMED", "CHECKED_IN"].includes(signUpStatusForClient || "") ? (
          <>
            <Button
              width="100%"
              isDisabled={true}
              accessibilityLabel={t`Join Event`}
              testID="event-detail-join-event-button"
            >
              {t`Join Event`}
            </Button>
            <Text
              color="text-secondary"
              variant="mainSmallSemiBold"
              mb={{ base: 4, md: 0 }}
              textAlign="center"
            >{t`Event is full`}</Text>
          </>
        ) : null}

        {isClosed &&
        !["CONFIRMED", "CHECKED_IN"].includes(signUpStatusForClient || "") ? (
          <>
            <Button
              width="100%"
              isDisabled={true}
              accessibilityLabel={t`Closed`}
              testID="event-detail-join-event-button"
            >
              {t`Closed`}
            </Button>
            <Text
              color="text-secondary"
              variant="mainSmallSemiBold"
              mb={{ base: 4, md: 0 }}
              textAlign="center"
            >{t`Closed`}</Text>
          </>
        ) : null}

        {isComplete &&
        !["CONFIRMED", "CHECKED_IN"].includes(signUpStatusForClient || "") ? (
          <>
            <Button
              width="100%"
              isDisabled={true}
              accessibilityLabel={t`Join Event`}
              testID="event-detail-join-event-button"
            >
              {t`Join Event`}
            </Button>
            <Text
              color="text-secondary"
              variant="mainSmallSemiBold"
              mb={{ base: 4, md: 0 }}
              textAlign="center"
            >{t`Event is complete`}</Text>
          </>
        ) : null}
      </VStack>
      <PopUpModal
        isOpen={isJoinEventModalOpen}
        upperTitle={t`Join Event`}
        cancelText={t`Cancel`}
        confirmText={t`Join`}
        onClose={(confirm: boolean) => {
          if (confirm) {
            handleJoinPress();
          } else {
            setIsJoinEventModalOpen(false);
          }
        }}
        content={
          <VStack>
            <Text color="text-secondary">
              {t`Are you sure you’d like to join ${event.eventSeries.name}?`}
            </Text>
          </VStack>
        }
        footerExtraContent={
          cancelAtWindow ? (
            <EventDetailCancelWindow
              wrapperStyle={{ borderRadius: Platform.OS === "web" ? "8px" : 0 }}
              cancelDeadlineAt={cancelAtWindow}
            />
          ) : undefined
        }
      />
    </Box>
  );
};

export const EventDetailJoinEventFragmentString = graphql`
  fragment EventDetailJoinEvent_event on Event {
    id
    accessStatusForClient(clientId: $memberId)
    signUpStatusForClient(clientId: $memberId)
    prePaymentRequirement
    maxGuestCount
    maxMemberCount
    minMemberCount
    unknownGuestsAllowed
    memberSignUpProxyAllowed
    minGuestCount
    signUps {
      edges {
        node {
          id
          registrations {
            edges {
              node {
                id
                registrant {
                  id
                }
              }
            }
          }
        }
      }
    }
    eventSeries {
      name
      facility {
        id
        club {
          slug
        }
      }
    }
    clientEligibleSignUpWindows(clientId: $memberId) {
      closeDaysAhead
      closeTime
      closesAt
      openTime
      opensAt
      cancelDeadlineAt
      cancelDeadlineDaysAhead
      cancelDeadlineTime
    }
  }
`;
